import { getMeta, setMeta } from '../data/appMeta';
import type { SqlDriver, Statement } from '../data/db/driver';
import { remoteUpsertStatement, type EntitySpec } from '../data/store/entity';
import {
  activitySpec,
  categorySpec,
  consumptionSpec,
  debtSpec,
  memberSpec,
  recurringSpec,
  spaceSpec,
  subcategorySpec,
  transactionSpec,
} from '../data/store/specs';
import type { Synced } from '../domain/model';
import { ACCESS_PREMIUM_KEY } from './readOnly';
import { SYNCED_TABLES, type Change, type RemoteRecord, type SyncTransport, type SyncedTable } from './types';

/**
 * One sync round: push everything in the outbox, then pull what changed on
 * the server and apply it. Push first, so the server has merged our own
 * edits before we read its version back.
 *
 * Pulled rows are written without outbox records (they came from the
 * server) and applied parents-first in one transaction. A field this device
 * changed while the round was running keeps its local value: it is still in
 * the outbox and wins on the next push.
 */
const SPECS: Record<SyncedTable, EntitySpec<Synced>> = {
  budget_spaces: spaceSpec,
  budget_members: memberSpec,
  categories: categorySpec,
  subcategories: subcategorySpec,
  recurring_templates: recurringSpec,
  transactions: transactionSpec,
  consumption_data: consumptionSpec,
  debt_details: debtSpec,
  activity_entries: activitySpec,
} as unknown as Record<SyncedTable, EntitySpec<Synced>>;

const isSynced = (t: string): t is SyncedTable => (SYNCED_TABLES as readonly string[]).includes(t);

export const CURSOR_KEY = 'sync.cursor';
export const LAST_SYNC_KEY = 'sync.lastAt';

export interface SyncReport {
  pushed: number;
  rejected: number;
  /** Kept on the device: the budget's owner has no Premium (the server said wait). */
  deferred: number;
  pulled: number;
  /** Budgets this account can see since this round (pulled whole). */
  gained: string[];
  /** Budgets this account lost access to this round (now hidden here). */
  lost: string[];
}

type OutboxRow = {
  seq: number;
  table_name: string;
  row_id: string;
  space_id: string | null;
  hlc: string;
  fields: string;
  prev: string | null;
};

/**
 * What a round may send. `holdOwnedBy`: the budgets this user owns stay
 * out of the push (Premium's cloud sync is off for them); their changes
 * wait in the outbox, in order, and go up the first round after Premium is
 * back. Held, not refused: a change the server refuses is dropped for good.
 */
export interface PushOptions {
  holdOwnedBy?: string | null;
  batchSize?: number;
}

/** The outbox rows of budgets owned by the user bound twice after it (plain ? for every driver). */
const HELD = `(sync_outbox.space_id IN (SELECT id FROM budget_spaces WHERE owner_user_id = ?)
  OR (sync_outbox.table_name = 'budget_spaces' AND sync_outbox.row_id IN (SELECT id FROM budget_spaces WHERE owner_user_id = ?)))`;

/** Changes waiting because Premium is off (see PushOptions.holdOwnedBy). */
export async function countHeld(db: SqlDriver, ownerUserId: string): Promise<number> {
  const [row] = await db.all<{ n: number }>(`SELECT COUNT(*) AS n FROM sync_outbox WHERE ${HELD}`, [ownerUserId, ownerUserId]);
  return Number(row?.n ?? 0);
}

/**
 * Send the outbox in order, in batches; accepted and refused changes both
 * leave it (refused ones never succeed). Changes the server defers (the
 * budget's owner has no Premium) stay, in order, for a later round; this
 * round moves past them.
 */
export async function pushOutbox(
  db: SqlDriver,
  transport: SyncTransport,
  { holdOwnedBy = null, batchSize = 500 }: PushOptions = {},
): Promise<{ pushed: number; rejected: number; deferred: number }> {
  let pushed = 0;
  let rejected = 0;
  let deferred = 0;
  let after = -1;
  for (;;) {
    const rows = await db.all<OutboxRow>(
      holdOwnedBy === null
        ? 'SELECT seq, table_name, row_id, space_id, hlc, fields, prev FROM sync_outbox WHERE seq > ? ORDER BY seq LIMIT ?'
        : `SELECT seq, table_name, row_id, space_id, hlc, fields, prev FROM sync_outbox WHERE seq > ? AND NOT ${HELD} ORDER BY seq LIMIT ?`,
      holdOwnedBy === null ? [after, batchSize] : [after, holdOwnedBy, holdOwnedBy, batchSize],
    );
    if (rows.length === 0) break;
    after = rows[rows.length - 1]!.seq;
    // Device-only tables (the local user row) never leave the device.
    const local = rows.filter((r) => !isSynced(r.table_name));
    const outgoing = rows.filter((r) => isSynced(r.table_name));
    const changes: Change[] = outgoing.map((r) => ({
      table: r.table_name as SyncedTable,
      id: r.row_id,
      spaceId: r.table_name === 'budget_spaces' ? r.row_id : (r.space_id ?? ''),
      hlc: r.hlc,
      fields: JSON.parse(r.fields) as Record<string, unknown>,
      ...(r.prev ? { prev: JSON.parse(r.prev) as Record<string, unknown> } : {}),
    }));
    const res = changes.length ? await transport.push(changes) : { accepted: [], rejected: [] };
    const done = new Set([...res.accepted, ...res.rejected.map((x) => x.hlc)]);
    const waiting = res.deferred?.length ?? 0;
    const settled = [...local, ...outgoing.filter((r) => done.has(r.hlc))].map((r) => r.seq);
    if (settled.length === 0 && waiting === 0) throw new Error('sync: the server settled none of the pushed changes');
    if (settled.length) await db.run(`DELETE FROM sync_outbox WHERE seq IN (${settled.map(() => '?').join(', ')})`, settled);
    pushed += res.accepted.length;
    rejected += res.rejected.length;
    deferred += waiting;
    if (res.rejected.length) console.warn('[sync] refused', res.rejected);
  }
  return { pushed, rejected, deferred };
}

/**
 * Fetch every change after the stored cursor and apply it. With `spaceId`:
 * the whole of one budget from the start (just joined: its rows are older
 * than this device's cursor), leaving the cursor alone.
 */
export async function pullRemote(db: SqlDriver, transport: SyncTransport, pageSize = 500, spaceId?: string): Promise<number> {
  let cursor = spaceId ? 0 : Number((await getMeta(db, CURSOR_KEY)) ?? 0);
  const records: RemoteRecord[] = [];
  for (;;) {
    const page = await transport.pull(cursor, pageSize, spaceId);
    records.push(...page.records);
    cursor = page.cursor;
    if (!page.hasMore) break;
  }
  if (records.length === 0) return 0;

  // Fields changed here since the push: keep them (they go up next round).
  const pending = new Map<string, Set<string>>();
  for (const r of await db.all<{ table_name: string; row_id: string; fields: string }>('SELECT table_name, row_id, fields FROM sync_outbox')) {
    const key = `${r.table_name}/${r.row_id}`;
    const set = pending.get(key) ?? new Set<string>();
    for (const f of Object.keys(JSON.parse(r.fields) as object)) set.add(f);
    pending.set(key, set);
  }

  const order = (t: SyncedTable) => SYNCED_TABLES.indexOf(t);
  const statements: Statement[] = [{ sql: 'PRAGMA defer_foreign_keys = ON' }];
  // People of a shared budget who are not this device's user: a local stand-in
  // row (never synced), so their budget and member rows can point at them.
  const people = new Set<string>();
  for (const rec of records) {
    const person = rec.table === 'budget_spaces' ? rec.fields.ownerUserId : rec.table === 'budget_members' ? rec.fields.userId : null;
    if (typeof person === 'string') people.add(person);
  }
  for (const id of people) {
    statements.push({
      sql: `INSERT OR IGNORE INTO users (id, display_name, email, kind, created_at, updated_hlc, deleted_at) VALUES (?, '', NULL, 'account', ?, '', NULL)`,
      params: [id, Date.now()],
    });
  }
  for (const rec of [...records].sort((a, b) => order(a.table) - order(b.table) || a.seq - b.seq)) {
    if (!isSynced(rec.table)) continue;
    const keep = pending.get(`${rec.table}/${rec.id}`);
    const fields = Object.fromEntries(Object.entries(rec.fields).filter(([k]) => !keep?.has(k)));
    fields.id = rec.id;
    const updatedHlc = Object.values(rec.fieldHlc).reduce((max, h) => (h > max ? h : max), '');
    const stmt = remoteUpsertStatement(SPECS[rec.table], fields, updatedHlc);
    if (stmt) statements.push(stmt);
  }
  if (!spaceId) {
    statements.push({
      sql: 'INSERT INTO app_meta (key, value) VALUES (?, ?) ON CONFLICT(key) DO UPDATE SET value = excluded.value',
      params: [CURSOR_KEY, String(cursor)],
    });
  }
  await db.batch(statements);
  return records.length;
}

export const ACCESS_KEY = 'sync.access';

/**
 * Keep this device's budgets in step with what the account may see.
 *
 * Gained (joined here or on another of your devices, or re-invited): the
 * whole budget comes down first, because its rows are older than this
 * device's cursor and a normal pull would bring entries without their
 * categories. Lost (removed by the owner, or left elsewhere): the budget
 * is hidden here; its rows stay, nothing about it goes up.
 */
export async function reconcileAccess(db: SqlDriver, transport: SyncTransport): Promise<{ gained: string[]; lost: string[] }> {
  const access = await transport.access();
  const now = access.map((a) => a.spaceId);
  const stored = await getMeta(db, ACCESS_KEY);
  const before: string[] = stored ? JSON.parse(stored) : [];
  // New to this account, or not (or no longer) on this device: e.g. left here, then invited again.
  const here = new Set((await db.all<{ id: string }>('SELECT id FROM budget_spaces WHERE deleted_at IS NULL')).map((r) => r.id));
  const gained = now.filter((id) => !before.includes(id) || !here.has(id));
  const lost = before.filter((id) => !now.includes(id));
  // A device that has never pulled (cursor 0): one normal pull brings every
  // budget whole and moves the cursor, so nothing comes down twice.
  const neverPulled = Number((await getMeta(db, CURSOR_KEY)) ?? 0) === 0;
  if (gained.length && neverPulled) await pullRemote(db, transport);
  else for (const id of gained) await pullRemote(db, transport, 500, id);
  const statements: Statement[] = lost.flatMap((id) => [
    { sql: 'UPDATE budget_spaces SET deleted_at = ? WHERE id = ? AND deleted_at IS NULL', params: [Date.now(), id] },
    { sql: 'DELETE FROM sync_outbox WHERE space_id = ? OR (table_name = ? AND row_id = ?)', params: [id, 'budget_spaces', id] },
  ]);
  const upsert = 'INSERT INTO app_meta (key, value) VALUES (?, ?) ON CONFLICT(key) DO UPDATE SET value = excluded.value';
  statements.push(
    { sql: upsert, params: [ACCESS_KEY, JSON.stringify(now)] },
    // Whose owner's Premium covers which budget (sync/readOnly.ts).
    { sql: upsert, params: [ACCESS_PREMIUM_KEY, JSON.stringify(Object.fromEntries(access.map((a) => [a.spaceId, a.premium])))] },
  );
  await db.batch(statements);
  return { gained, lost };
}

export async function syncOnce(db: SqlDriver, transport: SyncTransport, options: PushOptions = {}): Promise<SyncReport> {
  const { pushed, rejected, deferred } = await pushOutbox(db, transport, options);
  const { gained, lost } = await reconcileAccess(db, transport);
  const pulled = (await pullRemote(db, transport)) + gained.length;
  await setMeta(db, LAST_SYNC_KEY, String(Date.now()));
  return { pushed, rejected, deferred, pulled, gained, lost };
}
